import React from 'react';
import { Text, TouchableOpacity, View } from 'react-native';
import { HelperText, TextInput } from 'react-native-paper';
import {
  formatNumericStringWithCommas,
  sanitizeMoneyInput,
} from '../../../../utils/libs';
import {
  SERVICE_CHARGE_OPTIONS,
  SERVICE_HOUR_UNIT,
  type ServiceChargeType,
} from '../../../../utils/productKind';
import type { ProductFormData, ProductFormErrors } from '../productFormTypes';
import { useProductFormFieldTheme } from './productFormStyles';
import ProductUnitAffix from './ProductUnitAffix';
import ProductImagePicker from './ProductImagePicker';

/**
 * The Service segment of the product form: how the service is charged, its
 * price or hourly rate, an optional cost and an optional photo. No quantity,
 * minimum stock level, barcode or variants: a service keeps no stock. The
 * same tiles edit a saved service on the Edit Product screens.
 */

export interface ServiceChargeTilesProps {
  chargeType: ServiceChargeType;
  onChange: (chargeType: ServiceChargeType) => void;
  disabled?: boolean;
}

// The same tiles as the product's sell-type choice, so both segments read alike.
export const ServiceChargeTiles: React.FC<ServiceChargeTilesProps> = ({
  chargeType,
  onChange,
  disabled = false,
}) => {
  const { styles } = useProductFormFieldTheme();

  return (
    <>
      <View style={[styles.sectionHeader, styles.groupStart]}>
        <Text style={styles.sectionTitle}>How will you charge for this service?</Text>
      </View>
      <View style={styles.priceTypeTiles}>
        {SERVICE_CHARGE_OPTIONS.map(option => {
          const selected = chargeType === option.value;
          return (
            <TouchableOpacity
              key={option.value}
              style={[
                styles.priceTypeTile,
                selected ? styles.priceTypeTileSelected : null,
                disabled ? { opacity: 0.55 } : null,
              ]}
              activeOpacity={0.7}
              disabled={disabled}
              onPress={() => onChange(option.value)}
              accessibilityRole="radio"
              accessibilityState={{ selected, disabled }}
              testID={`service-charge-${option.value}`}>
              <View style={styles.priceTypeTileHeader}>
                <View
                  style={[
                    styles.priceTypeRadio,
                    selected ? styles.priceTypeRadioSelected : null,
                  ]}
                />
                <Text style={styles.priceTypeTileLabel}>{option.label}</Text>
              </View>
              <Text style={styles.priceTypeTileHint}>{option.hint}</Text>
            </TouchableOpacity>
          );
        })}
      </View>
    </>
  );
};

/** Field labels and the cost hint for a charge type. */
export const getServicePricingCopy = (chargeType: ServiceChargeType) =>
  chargeType === 'per_hour'
    ? {
        priceLabel: 'Rate per hour *',
        costLabel: 'Cost per hour (optional)',
        costHint: 'What an hour costs you, if anything. Used for profit reports.',
      }
    : {
        priceLabel: 'Selling Price *',
        costLabel: 'Cost (optional)',
        costHint:
          'What one service costs you, if anything. Used for profit reports.',
      };

export interface ServicePricingFieldsProps {
  formData: Pick<ProductFormData, 'buyingprice' | 'sellingprice'>;
  errors: ProductFormErrors;
  chargeType: ServiceChargeType;
  onChangeField: (field: string, value: string) => void;
}

export const ServicePricingFields: React.FC<ServicePricingFieldsProps> = ({
  formData,
  errors,
  chargeType,
  onChangeField,
}) => {
  const { theme, styles, inputOutlineProps, webPlaceholderTheme } =
    useProductFormFieldTheme();
  const copy = getServicePricingCopy(chargeType);
  // "/hr" inside the rate inputs, the way per-Kg prices show their unit
  const affix = ProductUnitAffix({
    priceType: chargeType === 'per_hour' ? 'unit' : 'item',
    unitOfMass: SERVICE_HOUR_UNIT,
    kind: 'price',
  });

  return (
    <>
      <View style={[styles.field, styles.groupStart]}>
        <TextInput
          mode="outlined"
          label={copy.priceLabel}
          value={formatNumericStringWithCommas(formData.sellingprice)}
          onChangeText={t => onChangeField('sellingprice', sanitizeMoneyInput(t))}
          keyboardType="numeric"
          placeholderTextColor={theme.colors.textSecondary}
          {...inputOutlineProps}
          {...webPlaceholderTheme}
          style={styles.input}
          error={!!errors.sellingprice}
          testID="service-selling-price-input"
          right={affix}
        />
        {errors.sellingprice ? (
          <HelperText type="error" visible>
            {errors.sellingprice}
          </HelperText>
        ) : null}
      </View>
      <View style={styles.field}>
        <TextInput
          mode="outlined"
          label={copy.costLabel}
          value={formatNumericStringWithCommas(formData.buyingprice)}
          onChangeText={t => onChangeField('buyingprice', sanitizeMoneyInput(t))}
          keyboardType="numeric"
          placeholderTextColor={theme.colors.textSecondary}
          {...inputOutlineProps}
          {...webPlaceholderTheme}
          style={styles.input}
          error={!!errors.buyingprice}
          testID="service-cost-input"
          right={affix}
        />
        <Text style={styles.fieldHint}>
          {errors.buyingprice || copy.costHint}
        </Text>
      </View>
    </>
  );
};

export interface ServicePhotoFieldProps {
  previewUri: string;
  onUploaded: (b64: string, local?: string) => void;
  onDiscard: () => void;
  onProcessingChange: (processing: boolean) => void;
}

/** The photo sits with the other fields: a service has no Additional Information. */
export const ServicePhotoField: React.FC<ServicePhotoFieldProps> = ({
  previewUri,
  onUploaded,
  onDiscard,
  onProcessingChange,
}) => {
  const { styles } = useProductFormFieldTheme();

  return (
    <>
      <Text style={[styles.sectionTitle, styles.groupStart]}>
        Photo (optional)
      </Text>
      <ProductImagePicker
        previewUri={previewUri}
        onUploaded={onUploaded}
        onDiscard={onDiscard}
        onProcessingChange={onProcessingChange}
        autoOpenWhenEmpty={false}
      />
    </>
  );
};
